"use client"

import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { controlFillClass, useSurface } from '@/components/ui/surface'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-brand-button)] text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground border-0 shadow-xs transition-[background-color,transform] duration-[180ms] hover:bg-primary-hover hover:-translate-y-0.5 active:scale-[0.98] motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/80 hover:shadow-sm active:scale-[0.98] focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border border-primary/60 text-foreground shadow-xs hover:border-primary hover:bg-primary/5 active:scale-[0.98] dark:border-primary/40 dark:hover:border-primary dark:hover:bg-primary/10',
        secondary:
          'border border-border text-foreground shadow-xs hover:bg-muted/60 hover:shadow-sm active:scale-[0.98]',
        ghost:
          'border border-border bg-transparent text-foreground shadow-xs hover:bg-muted/70 hover:text-foreground active:scale-[0.98] dark:hover:bg-muted',
        link: 'text-primary underline-offset-4 hover:underline hover:text-primary/80',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 px-6 has-[>svg]:px-4',
        marketing: 'min-h-[54px] px-[22px] py-[17px] text-[14px] gap-7 has-[>svg]:px-[22px]',
        touch: 'min-h-11 px-4 py-2.5 text-[13px] gap-4 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

type ButtonProps =
  React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean
      /** Alias for `className` (some callsites prefer `cssClass`) */
      cssClass?: string
    }

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, cssClass, variant, size, asChild = false, ...props }, ref) => {
    const Comp = (asChild ? Slot : 'button') as React.ElementType
    const surface = useSurface()
    const well = variant === 'outline' || variant === 'secondary' ? controlFillClass(surface) : undefined

    return (
      <Comp
        ref={ref}
        data-slot="button"
        data-component="button"
        data-variant={variant ?? "default"}
        data-on-surface={surface}
        className={cn(buttonVariants({ variant, size }), well, cssClass, className)}
        {...props}
      />
    )
  },
)

Button.displayName = 'Button'

export { Button, buttonVariants }
export type { ButtonProps }
